import type { CommunityEntry, SavedConfig } from "@cfg/api-contract";
import { deleteDraft, listDrafts, markAdopted, saveDraft, type Draft } from "@cfg/configurator-ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { useApp } from "../app-context.tsx";
import { useAuthUser } from "../auth.ts";
import { useCatalog } from "../catalog.ts";
import { configName, isAutoName, readableSummary } from "../config-name.ts";
import { useDesignDescriptions } from "../design-descriptions.ts";
import { useMe } from "../me.ts";
import { shortDate } from "../titles.ts";
import { FitPill, likesQueryKey, useMyCommunity, useMyLikes } from "./CommunityPanel.tsx";
import { PageHead } from "./PageHead.tsx";
import { DefaultEmptyState } from "./defaults.tsx";
import { DisplayNameForm } from "./DisplayNameForm.tsx";
import { RenameForm } from "./RenameForm.tsx";

interface Item {
  id: string;
  name: string;
  designId: string;
  version: string;
  params: Record<string, unknown>;
  updatedAt: string;
  kind: "draft" | "config";
}

/**
 * Yours: saved configurations, local drafts (anonymous) plus profile configs
 * (signed in), private to you. Your sets: community entries you tagged, with
 * your tags. Favorites: community entries you starred.
 */
export function MinePage() {
  const { brand, copy, locale, api, auth } = useApp();
  const user = useAuthUser(auth);
  const qc = useQueryClient();
  const Empty = brand.slots?.EmptyState ?? DefaultEmptyState;
  const me = useMe();
  const [editingName, setEditingName] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const filterId = useId();
  const catalog = useCatalog();
  const likes = useMyLikes();
  const tagged = useMyCommunity();
  const showFavorites = brand.config.features.publicGallery && !!user && !!api;
  /** The favorite just removed, for "Removed from Favorites · Undo". */
  const [unliked, setUnliked] = useState<CommunityEntry | null>(null);
  const likesKey = likesQueryKey(brand.config.id, user?.sub);
  const unlike = async (e: CommunityEntry) => {
    if (!api) return;
    await api.unlikePublic(e.id).catch(() => undefined);
    qc.setQueryData(likesKey, (old: CommunityEntry[] | undefined) => old?.filter((i) => i.id !== e.id));
    setUnliked(e);
  };
  const relike = async (e: CommunityEntry) => {
    if (!api) return;
    setUnliked(null);
    await api.likePublic(e.id).catch(() => undefined);
    qc.setQueryData(likesKey, (old: CommunityEntry[] | undefined) => [e, ...(old ?? []).filter((i) => i.id !== e.id)]);
  };
  const designs = new Map((catalog.data ?? []).map((d) => [d.id, d]));

  const drafts = useQuery({ queryKey: ["drafts", brand.config.id], queryFn: () => listDrafts(brand.config.id) });
  const configs = useQuery({
    queryKey: ["configs", user?.sub],
    queryFn: async () => (api && user ? (await api.listConfigs()).configs : []),
    enabled: !!(api && user),
  });

  // Adopt anonymous drafts into the profile on first sign-in.
  useEffect(() => {
    if (!api || !user || !drafts.data?.length) return;
    let cancelled = false;
    (async () => {
      for (const d of drafts.data) {
        if (cancelled) return;
        try {
          const cfg = await api.createConfig({ name: d.name, designId: d.designId, version: d.version, params: d.params });
          await markAdopted(d.id, cfg.id);
        } catch (err) {
          console.warn("Could not adopt draft", d.id, err);
        }
      }
      await qc.invalidateQueries({ queryKey: ["drafts"] });
      await qc.invalidateQueries({ queryKey: ["configs"] });
    })();
    return () => {
      cancelled = true;
    };
  }, [api, user, drafts.data, qc]);

  const items: Item[] = [
    ...(configs.data ?? []).map((c: SavedConfig): Item => ({ ...c, kind: "config" })),
    ...(drafts.data ?? []).map((d: Draft): Item => ({ ...d, kind: "draft" })),
  ].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const described = useDesignDescriptions(items);

  /**
   * How a saved bracket reads: the user's name with its dimensions under it, or, for a generated
   * name, its dimensions ("Wheel diameter 50 mm · …"). `code` is the stored parameter code, for the tooltip.
   */
  const naming = (it: Item) => {
    const d = described.get(`${it.designId}@${it.version}`);
    if (!d) return { custom: true, title: it.name, dims: "", code: it.name, auto: "" };
    const title = designs.get(it.designId)?.title ?? it.designId;
    const auto = configName(title, d.spec, it.params, d.defaults);
    const dims = readableSummary(d.spec, it.params, d.defaults, d.ui);
    const custom = !isAutoName(it.name, d.spec, it.params, d.defaults);
    return { custom, title: custom ? it.name : dims || title, dims: custom ? dims : "", code: custom ? auto : it.name, auto };
  };

  const rename = async (it: Item, name: string, auto: string) => {
    const next = name || auto || it.name;
    if (next !== it.name) {
      if (it.kind === "config" && api) await api.updateConfig(it.id, { name: next });
      else if (it.kind === "draft") {
        const d = drafts.data?.find((x) => x.id === it.id);
        if (d) await saveDraft({ ...d, name: next });
      }
      await qc.invalidateQueries({ queryKey: [it.kind === "draft" ? "drafts" : "configs"] });
    }
    setRenaming(null);
  };

  const displayName = me.data?.displayName;
  const myTags = new Set(tagged.data?.tags ?? []);

  /** A community entry as a row: design, its sets as pills (`highlight` marks the user's own), and an action. */
  const entryRow = (e: CommunityEntry, meta: string, action: React.ReactNode, highlight = false) => {
    const d = designs.get(e.designId);
    return (
      <li key={e.id} className="mine-item mine-item-liked">
        <a className="mine-item-link" href={`/design/${encodeURIComponent(e.designId)}?community=${encodeURIComponent(e.id)}`}>
          <span className="mine-thumb" aria-hidden="true">
            {d?.thumbnailUrl && <img src={d.thumbnailUrl} alt="" loading="lazy" />}
          </span>
          <span className="mine-item-text">
            <strong className="mine-item-name">{d?.title ?? e.designId}</strong>
            <span className="mine-item-meta muted small">{meta}</span>
            {e.fits.length > 0 && (
              <span className="mine-tags">
                {e.fits.map((f) => (
                  <span key={f.ref} className={highlight && myTags.has(`${e.id}#${f.ref}`) ? "mine-tag-own" : "mine-tag-other"}>
                    <FitPill fit={f} />
                  </span>
                ))}
              </span>
            )}
          </span>
        </a>
        {action}
      </li>
    );
  };

  const remove = async (item: Item) => {
    setConfirming(null);
    if (item.kind === "draft") await deleteDraft(item.id);
    else if (api) await api.deleteConfig(item.id);
    await qc.invalidateQueries({ queryKey: [item.kind === "draft" ? "drafts" : "configs"] });
  };

  return (
    <section className="page page-mine">
      <PageHead title={copy("mine.title")}>
        {!user && auth.enabled && (
          <p className="page-sub">
            {copy("auth.required")}{" "}
            <button type="button" className="link" onClick={() => void auth.signIn()}>
              {copy("auth.signIn")}
            </button>
          </p>
        )}
        {brand.config.features.publicGallery && user && api && (
          <div className="mine-profile">
            {displayName && !editingName && (
              <p className="muted small">
                {copy("profile.displayName")}: <strong>{displayName}</strong>{" "}
                <button type="button" className="link" onClick={() => setEditingName(true)}>
                  {copy("profile.change")}
                </button>
              </p>
            )}
            {editingName && <DisplayNameForm initial={displayName} onDone={() => setEditingName(false)} />}
          </div>
        )}
      </PageHead>
      <section className="mine-section" aria-labelledby={`${filterId}-yours`}>
      {showFavorites && (
        <h2 className="mine-section-title" id={`${filterId}-yours`}>
          {copy("mine.yours")}
        </h2>
      )}
      {items.length === 0 ? (
        <Empty title={copy("mine.empty")} />
      ) : (
        <ul className="mine-list">
          {items.map((it) => {
            const key = `${it.kind}:${it.id}`;
            const d = designs.get(it.designId);
            const n = naming(it);
            if (renaming === key) {
              return (
                <li key={key} className="mine-item is-renaming">
                  <span className="mine-thumb" aria-hidden="true">
                    {d?.thumbnailUrl && <img src={d.thumbnailUrl} alt="" loading="lazy" />}
                  </span>
                  <RenameForm className="mine-rename" initial={n.custom ? it.name : ""} onSave={(name) => rename(it, name, n.auto)} onCancel={() => setRenaming(null)} />
                </li>
              );
            }
            return (
              <li key={key} className={`mine-item${confirming === key ? " is-confirming" : ""}`}>
                <a className="mine-item-link" href={`/design/${encodeURIComponent(it.designId)}?c=${encodeURIComponent(it.id)}`}>
                  <span className="mine-thumb" aria-hidden="true">
                    {d?.thumbnailUrl && <img src={d.thumbnailUrl} alt="" loading="lazy" />}
                  </span>
                  <span className="mine-item-text">
                    <strong className="mine-item-name" title={copy("mine.codeHint", { code: n.code })}>
                      {n.title}
                    </strong>
                    {n.dims && <span className="mine-item-dims small">{n.dims}</span>}
                    <span className="mine-item-meta muted small">
                      <span className="mine-item-design">{d?.title ?? it.designId}</span>
                      {" · "}
                      <time className="mine-item-date" dateTime={it.updatedAt} title={new Date(it.updatedAt).toLocaleString(locale)}>
                        {shortDate(it.updatedAt, locale)}
                      </time>
                      {it.kind === "draft" && <span className="mine-item-local"> · {copy("mine.onDevice")}</span>}
                    </span>
                  </span>
                </a>
                {confirming === key ? (
                  <span className="mine-delete-confirm" role="group" aria-label={copy("mine.deleteConfirm", { name: n.title })}>
                    <span className="mine-delete-ask small">{copy("mine.deleteConfirm", { name: n.title })}</span>
                    <button type="button" className="cfg-btn mine-delete-yes" onClick={() => void remove(it)}>
                      {copy("mine.delete")}
                    </button>
                    <button type="button" className="link mine-delete-no" autoFocus onClick={() => setConfirming(null)}>
                      {copy("mine.cancel")}
                    </button>
                  </span>
                ) : (
                  <span className="mine-item-actions">
                    <button type="button" className="link mine-rename-btn" aria-label={copy("mine.renameLabel", { name: n.title })} onClick={() => setRenaming(key)}>
                      {copy("mine.rename")}
                    </button>
                    <button type="button" className="link mine-delete" aria-label={copy("mine.deleteLabel", { name: n.title })} onClick={() => setConfirming(key)}>
                      {copy("mine.delete")}
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      </section>
      {showFavorites && (
        <section className="mine-section mine-tagged" aria-labelledby={`${filterId}-tagged`}>
          <h2 className="mine-section-title" id={`${filterId}-tagged`}>
            {copy("mine.tagged")}
          </h2>
          {!tagged.data?.items.length ? (
            <p className="muted mine-section-empty">{copy("mine.taggedEmpty")}</p>
          ) : (
            <ul className="mine-list">{tagged.data.items.map((e) => entryRow(e, copy("community.taggers", { names: e.taggers.join(", ") }), null, true))}</ul>
          )}
        </section>
      )}
      {showFavorites && (
        <section className="mine-section mine-liked" aria-labelledby={`${filterId}-liked`}>
          <h2 className="mine-section-title" id={`${filterId}-liked`}>
            {copy("mine.liked")}
          </h2>
          {unliked && (
            <p className="muted small mine-undo" role="status">
              {copy("community.unfavorited")}{" "}
              <button type="button" className="link" onClick={() => void relike(unliked)}>
                {copy("community.undo")}
              </button>
            </p>
          )}
          {!likes.data?.length ? (
            <p className="muted mine-section-empty">{copy("mine.favoritesEmpty")}</p>
          ) : (
            <ul className="mine-list">
              {likes.data.map((e) =>
                entryRow(
                  e,
                  copy("community.taggers", { names: e.taggers.join(", ") }),
                  <button type="button" className="cfg-chip mine-unlike is-liked" aria-label={copy("mine.unfavoriteLabel", { name: designs.get(e.designId)?.title ?? e.designId })} onClick={() => void unlike(e)}>
                    ★ {copy("mine.unfavorite")}
                  </button>,
                ),
              )}
            </ul>
          )}
        </section>
      )}
    </section>
  );
}
